/**
 * Patient API - full CRUD (clinical has write access).
 */
import { apiGet, apiGetList, apiPost, apiPostRaw, apiPut } from "./client";
import type {
  Patient,
  CreatePatientRequest,
  UpdatePatientRequest,
  PaginatedResponse,
  CodedConcept,
} from "@/types";

type PatientSearchRequest = {
  first_name?: string;
  last_name?: string;
  dob?: string;
  mrn?: string;
  phone?: string;
  page?: number;
  per_page?: number;
};

function parseNameParts(name?: string): { first_name?: string; last_name?: string } {
  const raw = (name ?? "").trim();
  if (!raw) return {};

  if (raw.includes(",")) {
    const [last, first] = raw.split(",").map((part) => part.trim());
    return {
      first_name: first || undefined,
      last_name: last || undefined,
    };
  }

  const parts = raw.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return {
      first_name: parts[0],
      last_name: parts.slice(1).join(" "),
    };
  }

  return { first_name: raw };
}

async function searchPatientsPage(params: PatientSearchRequest): Promise<PaginatedResponse<Patient>> {
  return apiGetList<Patient>("/patients/search", { params });
}

/** List patients with optional filters. */
export function listPatients(params?: {
  page?: number;
  per_page?: number;
  search?: string;
  status?: string;
  phone?: string;
  ssn?: string;
  dob?: string;
  external_id?: string;
  date_from?: string;
  date_to?: string;
  /** Provider UUID, or "me" for the signed-in user */
  primary_provider_id?: string;
  /** "private", "corporate", or a corporate client id. */
  paid_by?: string;
}): Promise<PaginatedResponse<Patient>> {
  return apiGetList<Patient>("/patients", { params });
}

/** Advanced find patient by multiple parameters (OpenEMR Patient Finder parity). */
export function findPatients(params: {
  name?: string;
  phone?: string;
  ssn?: string;
  dob?: string;
  external_id?: string;
  mrn?: string;
}): Promise<Patient[]> {
  const trimmedName = params.name?.trim();
  const hasStructured = Boolean(params.dob || params.mrn || params.phone);

  if (!trimmedName && !hasStructured && !params.external_id?.trim()) {
    return Promise.resolve([]);
  }

  const request = hasStructured
    ? searchPatientsPage({
        ...parseNameParts(trimmedName),
        dob: params.dob,
        mrn: params.mrn,
        phone: params.phone,
        page: 1,
        per_page: 100,
      })
    : apiGetList<Patient>("/patients", {
        params: {
          search: trimmedName,
          page: 1,
          per_page: 100,
        },
      });

  return request.then((resp) => {
    const extId = params.external_id?.trim().toLowerCase();
    if (!extId) return resp.data;
    return resp.data.filter((patient) => (patient.external_id ?? "").toLowerCase().includes(extId));
  });
}

/** Get a single patient by ID. */
/** Display names for patient IDs (list screens). Unknown IDs are omitted. */
export function getPatientDisplayNames(ids: string[]): Promise<Record<string, string>> {
  return apiPost<Record<string, string>>("/patients/display-names", { ids });
}

export function getPatient(id: string): Promise<Patient> {
  return apiGet<Patient>(`/patients/${id}`);
}

/** Coded value sets for the demographics form - § 170.315(a)(5). */
export interface DemographicValueSets {
  races: CodedConcept[];
  race_categories: CodedConcept[];
  ethnicity_categories: CodedConcept[];
  ethnicities: CodedConcept[];
  languages: CodedConcept[];
  sexual_orientations: CodedConcept[];
  gender_identities: CodedConcept[];
  pronouns: CodedConcept[];
  sex_parameters: CodedConcept[];
}

export function getDemographicValueSets(): Promise<DemographicValueSets> {
  return apiGet<DemographicValueSets>("/demographics/value-sets");
}

/** Create a new patient record. */
export function createPatient(data: CreatePatientRequest): Promise<Patient> {
  return apiPost<Patient>("/patients", data);
}

/** Update an existing patient record. */
export function updatePatient(
  id: string,
  data: UpdatePatientRequest,
): Promise<Patient> {
  return apiPut<Patient>(`/patients/${id}`, data);
}

/** Search patients by name or MRN without placing PHI in the URL. */
export function searchPatients(query: string): Promise<Patient[]> {
  const trimmed = query.trim();
  if (!trimmed) return Promise.resolve([]);

  return apiPostRaw<PaginatedResponse<Patient>>("/patients/search", {
    search: trimmed,
    page: 1,
    per_page: 8,
  }).then((resp) => resp.data);
}

/** Get a patient by their Medical Record Number. */
export function getPatientByMRN(mrn: string): Promise<Patient> {
  return apiGet<Patient>("/patients/by-mrn", { params: { mrn } });
}
